import React, { useMemo } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm, FormProvider, useFormContext } from 'react-hook-form';
import { Message } from '@blink/components/src/constants/messages';
import { useDispatch, useSelector } from 'react-redux';

import { WithdrawalAmountField } from './WithdrawalAmountField';

import { OperationStatusType } from '@/features/Modals/ui/OperationStatusModal';
import { Store } from '@/shared/store';
import { useModal, useWithdrawal } from '@/features/Modals/hooks';
import { ModalName } from '@/shared/store/modals/constants';
import { showModalAction } from '@/shared/store/modals/actions';
import { formatWithMaxPrecision } from '@/shared/utils/helpers';
import WithdrawalModalSchema, {
    WithdrawalModalData,
} from '@/shared/validations/withdrawalModal.schema';
import { Button, Text, TipCard, TooltipWrapper } from '@/shared/ui';
import { SummaryRow } from '@/widgets/Modals';

const WithdrawalModalTexts = {
    title: 'How much do you want to withdraw?',
    tip: 'Arkis Liquidity (LP) tokens automatically earn interest proportional to your share of the pool.',
    tooltips: {
        vaultUtilization: 'Total token supply being used by borrowers for their operations',
        vaultBalance:
            'Vault Balance shows the available tokens that have not been used by borrowers',
        netApy: 'Net APY is the total annual yield from your deposit, including interest and rewards.',
    },
};

enum VaultsWithdrawalModalFields {
    AMOUNT = 'amount',
}

export const VaultsWithdrawalModal = () => {
    const { modalProps } = useModal();
    const dispatch = useDispatch();
    const walletAddress = useSelector((state: Store) => state.wallets.active.address);
    const {
        tokenSymbol,
        tokenAddress,
        totalPosition,
        totalDepositThreshold,
        decimals,
        poolUtilization,
        agreementAddress,
        poolBalance,
        refresh,
        netApy,
    } = modalProps || {};

    const methods = useForm<WithdrawalModalData>({
        resolver: yupResolver(WithdrawalModalSchema),
        reValidateMode: 'onChange',
    });

    const { makeWithdrawal } = useWithdrawal();

    const currentModal = {
        activeModal: ModalName.VAULT_WITHDRAWAL_MODAL,
        modalTitle: 'Supply Liquidity',
        modalProps,
    };

    const onSubmit = async (data: WithdrawalModalData) => {
        if (!walletAddress) {
            dispatch(
                showModalAction({
                    modalName: ModalName.CONNECT_WALLET_MODAL,
                    modalTitle: 'Connect Wallet',
                }),
            );
            return;
        }

        dispatch(
            showModalAction({
                modalName: ModalName.OPERATION_STATUS_MODAL,
                modalProps: {
                    type: OperationStatusType.Pending,
                    description: Message.PENDING_TEXT,
                },
                previousModal: currentModal,
            }),
        );

        await makeWithdrawal({
            amount: data[VaultsWithdrawalModalFields.AMOUNT],
            agreementAddress,
            tokenAddress,
            totalPosition,
            poolBalance,
            decimals,
        });

        if (refresh) refresh();
    };

    return (
        <FormProvider {...methods}>
            <form onSubmit={methods.handleSubmit(onSubmit)} className='w-[448px] pt-7'>
                <Text className='mb-3 mt-2'>{WithdrawalModalTexts.title}</Text>
                <WithdrawalAmountField name={VaultsWithdrawalModalFields.AMOUNT} />
                <div className='mt-5'>
                    <TipCard text={WithdrawalModalTexts.tip} title='Tip' />
                </div>
                <div className='border-b pb-7 mb-7 mt-5 border-border-opaque'>
                    <SummaryRow
                        label='Net APY'
                        value={Number.isNaN(Number(netApy)) ? 'N/A' : `${netApy} %`}
                    >
                        <TooltipWrapper text={WithdrawalModalTexts.tooltips.netApy} />
                    </SummaryRow>

                    <SummaryRow label='Vaults Utilization' value={`${poolUtilization}%`}>
                        <TooltipWrapper text={WithdrawalModalTexts.tooltips.vaultUtilization} />
                    </SummaryRow>

                    <SummaryRow label='Vault Cap' className={'border-none'}>
                        <div className='flex gap-2'>
                            <Text
                                type={Text.types.Paragraph}
                                fontWeight={Text.weights.SemiBold}
                            >{`${formatWithMaxPrecision(
                                poolBalance,
                                decimals,
                            )} ${tokenSymbol}`}</Text>
                            <Text
                                type={Text.types.Paragraph}
                                className='text-content-secondary'
                            >{`/ ${totalDepositThreshold} ${tokenSymbol}`}</Text>
                        </div>
                        <TooltipWrapper text={WithdrawalModalTexts.tooltips.vaultBalance} />
                    </SummaryRow>
                </div>

                <SubmitButton />
            </form>
        </FormProvider>
    );
};

function SubmitButton() {
    const walletAddress = useSelector((state: Store) => state.wallets.active.address);
    const { watch, getFieldState } = useFormContext();
    const { modalProps } = useModal();
    const { tokenSymbol, poolBalance } = modalProps || {};
    const amount = watch(VaultsWithdrawalModalFields.AMOUNT);
    const { error } = getFieldState(VaultsWithdrawalModalFields.AMOUNT) || {};

    const getSubmitButtonText = () => {
        return walletAddress ? `Withdraw ${tokenSymbol}` : 'Connect Wallet';
    };

    const isDisabled = useMemo(() => {
        if (error) return true;
        if (!amount || Number(amount) === 0) return true;
        if (!amount && !!walletAddress) return true;
        return Number(poolBalance) < Number(amount);
    }, [poolBalance, amount, walletAddress, error]);

    return (
        <Button
            size={Button.sizes.Large}
            disabled={isDisabled}
            className='w-full font-semibold'
            type='submit'
        >
            {getSubmitButtonText()}
        </Button>
    );
}
